import type {
  CompanyLinks,
  CompanyProfile,
  CompanyTechnology,
  FundingEvent,
  ParentOrganization,
  Suborganization,
} from "./types";

// Apollo's organization payload is plan-dependent and not published as a stable schema.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnknownRecord = any;

const record = (value: unknown): UnknownRecord =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const num = (value: unknown): number | null => {
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : null;
};
const strings = (value: unknown): string[] =>
  list(value).map(text).filter(Boolean) as string[];

const parentFrom = (
  value: unknown,
  fallbackId?: unknown
): ParentOrganization | null => {
  const org = record(value);
  const id = text(org.id) ?? text(fallbackId);
  const name = text(org.name);
  if (!id && !name) return null;
  return {
    id,
    name,
    websiteUrl: text(org.website_url) ?? text(org.primary_domain),
  };
};

export function normalizeApolloOrganization(
  payload: unknown,
  companyDomain: string
): CompanyProfile | null {
  const body = record(payload);
  const nestedOrg = body.organization ?? list(body.organizations)[0];
  // API may send `{ organization: {...} }` or the organization object itself.
  const org = record(
    nestedOrg ??
      (text(body.id) || text(body.name) || text(body.primary_domain)
        ? body
        : {})
  );
  if (!text(org.id) && !text(org.name)) return null;

  const departments = record(org.departmental_head_count);
  const departmentalHeadCount: Record<string, number> = {};
  for (const [key, value] of Object.entries(departments)) {
    const count = num(value);
    if (count && count > 0) departmentalHeadCount[key] = count;
  }

  const fundingEvents: FundingEvent[] = list(org.funding_events)
    .map(record)
    .map((event) => ({
      id: text(event.id),
      date: text(event.date),
      type: text(event.type),
      amount:
        text(event.amount) ??
        (num(event.amount) !== null ? String(num(event.amount)) : null),
      currency: text(event.currency),
      investors: text(event.investors),
      newsUrl: text(event.news_url),
    }))
    .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));

  const suborganizations: Suborganization[] = list(org.suborganizations)
    .map(record)
    .map((sub) => ({
      id: text(sub.id),
      name: text(sub.name),
      industries: strings(sub.industries).length
        ? strings(sub.industries)
        : text(sub.industry)
          ? [text(sub.industry) as string]
          : [],
      estimatedNumEmployees: num(sub.estimated_num_employees),
      country: text(sub.country),
      websiteUrl: text(sub.website_url) ?? text(sub.primary_domain),
    }));

  const technologies: CompanyTechnology[] = list(org.current_technologies)
    .map(record)
    .map((tech) => ({
      name: text(tech.name) ?? "",
      category: text(tech.category),
    }))
    .filter((tech) => tech.name);

  const technologyNames = strings(org.technology_names)
    .filter(
      (name) =>
        !technologies.some(
          (tech) => tech.name.toLowerCase() === name.toLowerCase()
        )
    )
    .map((name) => ({ name, category: null }));

  const links: CompanyLinks = {
    linkedin: text(org.linkedin_url),
    twitter: text(org.twitter_url),
    facebook: text(org.facebook_url),
    angellist: text(org.angellist_url),
    crunchbase: text(org.crunchbase_url),
    website: text(org.website_url),
    blog: text(org.blog_url),
  };

  return {
    companyDomain,
    name: text(org.name) ?? companyDomain,
    apolloOrganizationId: text(org.id),
    logoUrl: text(org.logo_url),
    websiteUrl: text(org.website_url),
    blogUrl: text(org.blog_url),
    industry: text(org.industry),
    industries: [
      ...new Set([
        ...strings(org.industries),
        ...strings(org.secondary_industries),
      ]),
    ],
    keywords: strings(org.keywords).slice(0, 40),
    foundedYear: num(org.founded_year),
    shortDescription: text(org.short_description),
    seoDescription: text(org.seo_description),
    rawAddress: text(org.raw_address) ?? text(org.street_address),
    city: text(org.city),
    state: text(org.state),
    country: text(org.country),
    phone:
      text(org.phone) ??
      text(record(org.primary_phone).sanitized_number) ??
      text(record(org.primary_phone).number),
    annualRevenue: num(org.annual_revenue),
    annualRevenuePrinted: text(org.annual_revenue_printed),
    totalFunding: num(org.total_funding),
    totalFundingPrinted: text(org.total_funding_printed),
    latestFundingStage: text(org.latest_funding_stage),
    latestFundingRoundDate: text(org.latest_funding_round_date),
    fundingEvents,
    estimatedNumEmployees: num(org.estimated_num_employees),
    headcountGrowthSixMonth: num(org.organization_headcount_six_month_growth),
    headcountGrowthTwelveMonth: num(
      org.organization_headcount_twelve_month_growth
    ),
    headcountGrowthTwentyFourMonth: num(
      org.organization_headcount_twenty_four_month_growth
    ),
    departmentalHeadCount,
    publiclyTradedSymbol: text(org.publicly_traded_symbol),
    publiclyTradedExchange: text(org.publicly_traded_exchange),
    ownedByOrganization: parentFrom(
      org.owned_by_organization,
      org.owned_by_organization_id
    ),
    ownedByChain: strings(org.owned_by_chain),
    ultimateParentOrganization: parentFrom(org.ultimate_parent_organization),
    suborganizations,
    numSuborganizations:
      num(org.num_suborganizations) ?? (suborganizations.length || null),
    technologies: [...technologies, ...technologyNames].slice(0, 60),
    links,
    enrichmentStatus: "ready",
    enrichedAt: new Date().toISOString(),
  };
}
